<template>
    <section name="CreateEditNotice">
        <information-modal
            :title="title"
            v-model="showCreateNoticeModal"
            :show-footer="true"
            width="35.5em"
            @dismiss="handleDismiss()">
            <template #body>
                <div class="container-fluid">
                    <div class="row">
                        <div class="col">
                            <label
                                for="headline"
                                aria-label="Subject line of the notice"
                                >Headline:</label
                            >
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-11 offset-1">
                            <b-input
                                name="headline"
                                type="text"
                                :error="errors.headline"
                                required="required"
                                v-model="notice.headline" />
                        </div>
                    </div>
                    <div class="row mt-2">
                        <div class="col">
                            <label
                                for="body"
                                aria-label="Main content of the notice"
                                class="align-middle"
                                >Body:</label
                            >
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-11 offset-1">
                            <b-textarea
                                name="body"
                                aria-required="true"
                                required="required"
                                v-model="notice.body" />
                        </div>
                    </div>
                    <div class="row mt-2">
                        <div class="col align-content-center">
                            <label>Select Recipients by:</label>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-11 offset-1">
                            <div class="hstack gap-2 w-100">
                                <div
                                    class="form-check ps-0"
                                    style="width: 11em">
                                    <input
                                        id="recipientAgencyRadio"
                                        name="recipientRadio"
                                        type="radio"
                                        value="agency"
                                        :disabled="isEditMode"
                                        v-model="recipientMode" />
                                    <label
                                        class="form-check-label ps-2"
                                        for="recipientAgencyRadio"
                                        :disabled="isEditMode"
                                        >Agency:</label
                                    >
                                </div>

                                <SelectField
                                    class="w-100"
                                    v-model="notice.agency"
                                    :select-items="oriStore.getOrisForSelectList"
                                    mode="multiple"
                                    :error="errors.agency"
                                    :min="1"
                                    :include-show-all-option="true"
                                    :hide-selected="false"
                                    :close-on-select="false"
                                    :is-disabled="recipientMode != 'agency'"></SelectField>
                            </div>
                            <div class="hstack gap-2 w-100 mt-2">
                                <div
                                    class="form-check ps-0"
                                    style="width: 11em">
                                    <input
                                        id="recipientConditionRadio"
                                        name="recipientRadio"
                                        type="radio"
                                        value="condition"
                                        :disabled="isEditMode"
                                        v-model="recipientMode" />
                                    <label
                                        class="form-check-label ps-2"
                                        for="recipientConditionRadio"
                                        :disabled="isEditMode"
                                        >Condition:</label
                                    >
                                </div>
                                <SelectField
                                    class="w-100"
                                    v-model="notice.condition"
                                    mode="single"
                                    :select-items="scheduledNoticeStore.conditions"
                                    :error="errors.condition"
                                    :is-disabled="isEditMode || recipientMode != 'condition'"></SelectField>
                            </div>
                        </div>
                    </div>
                    <div class="row mt-2">
                        <div class="col-4 align-content-center">
                            <label>Email Notice to:</label>
                        </div>
                        <div class="col-8">
                            <Multiselect
                                v-model="notice.emailRecipientsArray"
                                :options="scheduledNoticeStore.emailRecipients"
                                :mode="'tags'"
                                :close-on-select="false"
                                :label="'label'"
                                :rules="['min:1']"
                                :native-support="true"
                                :can-clear="true"
                                :hide-selected="true"
                                open-direction="bottom"
                                autocomplete="n/a"
                                @clear.prevent="handleClear"
                                :classes="{
                                    tag: 'text-sm py-0.5 pl-2 rounded mr-1 mb-1 flex items-center tagwidth-100 text-truncate rtl:pl-0 rtl:pr-2 rtl:mr-0 rtl:ml-1',
                                }">
                                <template v-slot:tag="{option, handleTagRemove, disabled}">
                                    <div
                                        class="multiselect-tag"
                                        :class="{
                                            'is-disabled': disabled,
                                        }">
                                        <div
                                            class="d-inlineblock hstack bg-secondary text-white text-sm py-0.5 pl-2 rounded mr-1 mb-1 flex items-center rtl:pl-0 rtl:pr-2 rtl:mr-0 rtl:ml-1"></div>
                                        <span
                                            tabindex="-1"
                                            class="tagwidth-150 text-truncate">
                                            {{ option.label }}
                                        </span>
                                        <span
                                            v-if="!disabled"
                                            class="multiselect-tag-remove"
                                            @click.prevent="handleTagRemove(option, $event)">
                                            <span class="multiselect-tag-remove-icon"></span>
                                        </span>
                                    </div>
                                </template>
                            </Multiselect>
                            <span
                                class="invalid-feedback"
                                role="alert"
                                v-if="errors.emailRecipients">
                                {{ errors.emailRecipients }}
                            </span>
                        </div>
                    </div>
                    <div class="row mt-2" v-if="scheduledNoticeStore.availableReports?.length > 0">
                        <div class="col-4">
                            <label>Attach Report(s):</label>
                        </div>
                        <div class="col-8">
                            <Multiselect
                                v-model="notice.reports"
                                :options="scheduledNoticeStore.availableReports"
                                :mode="'tags'"
                                :close-on-select="false"
                                :label="'label'"
                                :rules="[]"
                                :native-support="true"
                                :can-clear="true"
                                :hide-selected="true"
                                open-direction="bottom"
                                autocomplete="n/a"
                                @clear.prevent="handleClear"
                                :classes="{
                                    tag: 'text-sm py-0.5 pl-2 rounded mr-1 mb-1 flex items-center tagwidth-100 text-truncate rtl:pl-0 rtl:pr-2 rtl:mr-0 rtl:ml-1',
                                }">
                                <template v-slot:tag="{option, handleTagRemove, disabled}">
                                    <div
                                        class="multiselect-tag"
                                        :class="{
                                            'is-disabled': disabled,
                                        }">
                                        <div
                                            class="d-inlineblock hstack bg-secondary text-white text-sm py-0.5 pl-2 rounded mr-1 mb-1 flex items-center rtl:pl-0 rtl:pr-2 rtl:mr-0 rtl:ml-1"></div>
                                        <span
                                            tabindex="-1"
                                            class="tagwidth-150 text-truncate">
                                            {{ option.label }}
                                        </span>
                                        <span
                                            v-if="!disabled"
                                            class="multiselect-tag-remove"
                                            @click.prevent="handleTagRemove(option, $event)">
                                            <span class="multiselect-tag-remove-icon"></span>
                                        </span>
                                    </div>
                                </template>
                            </Multiselect>
                            <span
                                class="invalid-feedback"
                                role="alert"
                                v-if="errors.reports">
                                {{ errors.reports }}
                            </span>
                        </div>
                    </div>
                    <div
                        class="row mt-2"
                        v-if="props.item?.id > 0">
                        <div class="col-4 align-content-center">
                            <label> Most Recent Execution Date:</label>
                        </div>
                        <div class="col-8 align-content-center">
                            {{ props.item.last_run?.replace("0001-01-01", "") }}
                        </div>
                    </div>
                    <div class="row mt-2">
                        <div class="col-4 align-content-center">
                            <label>Run Date:</label>
                        </div>
                        <div class="col-8">
                            <Datepicker
                                v-model="notice.scheduleNotification"
                                :format="dateFormat"
                                :clearable="false"
                                required
                                input-class-name="form-control"
                                aria-label="Schedule notification"
                                :enable-time-picker="false"
                                utc="preserve"
                                text-input
                                prevent-min-max-navigation
                                :start-date="new Date(Date.now())"
                                exactMatch>
                            </Datepicker>
                        </div>
                    </div>
                    <div class="row mt-2">
                        <div class="col-4 align-content-center">
                            <label for="repeat">Repeat:</label>
                        </div>
                        <div class="col-8">
                            <SelectField
                                :can-clear="false"
                                v-model="notice.repeat"
                                :select-items="scheduledNoticeStore.repeatOptions"
                                :is-required="true"
                                open-direction="top"></SelectField>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="row mt-2">
                    <div class="col-sm-12">
                        <div class="hstack gap-2 align-end">
                            <button
                                class="btn btn-danger width-75 margin-right-5"
                                @click.prevent="handleDelete()"
                                v-if="props.item?.id > 0">
                                Delete
                            </button>
                            <button
                                class="btn btn-secondary width-75"
                                @click.prevent="handleDismiss()">
                                Cancel
                            </button>
                            <button
                                :disabled="submitDisabled"
                                class="btn btn-primary width-75"
                                @click.prevent="submitNotice()">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </template>
        </information-modal>
    </section>
</template>
<script setup lang="ts">
import SelectField from "@/components/Misc/SelectField.vue";

import Datepicker from "@vuepic/vue-datepicker";
import {useScheduledNoticeStore} from "@/stores/scheduledNoticeStore";
import {useOriStore} from "@/stores/oriStore";
import {useFormValidation} from "@/validation";
import {computed, onMounted, ref, watch} from "vue";
import {NoticeModel, NotificationEmailType, ScheduledItem} from "./types";
import {Ori, SelectItem} from "../../types";
import {BSelect, BInput, BTextarea, BAgency} from "../Fields";
import {array, date, object, string} from "yup";
import Multiselect from "@vueform/multiselect";
import InformationModal from "../Misc/InformationModal.vue";
const props = defineProps<{
    item: ScheduledItem;
    title: string;
    showModal: boolean;
}>();

const scheduledNoticeStore = useScheduledNoticeStore();
const oriStore = useOriStore();
const recipientMode = ref("agency" as "agency" | "condition");
const dateFormat = "yyyy-MM-dd";
const notice = ref(new NoticeModel(props.item));
const emit = defineEmits(["dismiss", "submit"]);
const allOris = ref([] as Ori[]);
var submitDisabled = ref(false);
const isEditMode = computed(() => {
    return props.item?.id > 0;
});
var showCreateNoticeModal = ref(false);
const schema = object<NoticeModel>({
    headline: string().required().label("Headline"),
    scheduleNotification: date().required().label("Run Date"),
    emailRecipients: array().required().min(1).label("Email Notice to"),
    agency: array()
        .label("Agency")
        .nullable()
        .when([], {
            is: () => recipientMode.value == "agency",
            then: (schema) => schema.required().min(1),
        }),
    condition: string()
        .label("Condition")
        .nullable()
        .when([], {
            is: () => recipientMode.value == "condition",
            then: (schema) => schema.required(),
        }),
});

const {handleSubmit, errors} = useFormValidation();
async function submitNotice() {
    submitDisabled.value = true;

    if (await handleSubmit(schema, notice.value)) {
        if (props.item?.id > 0) {
            await scheduledNoticeStore.editScheduledTask(props.item.id, notice.value);
        } else {
            await scheduledNoticeStore.createScheduledTask(notice.value);
        }

        await scheduledNoticeStore.getScheduledNotices();

        handleDismiss();        
    }
    
    submitDisabled.value = false;
}

function handleDismiss() {
    emit("dismiss");
}

async function handleDelete() {
    if (props.item?.id > 0) {
        await scheduledNoticeStore.deleteScheduledTask(props.item.id);
    }

    await scheduledNoticeStore.getScheduledNotices();

    handleDismiss();
}

function handleClear() {
    notice.value.emailRecipientsArray = [] as NotificationEmailType[];
}

onMounted(async () => {
    
    submitDisabled.value = false;
    scheduledNoticeStore.getConditions();
    scheduledNoticeStore.getRepeatOptions();
    scheduledNoticeStore.getEmailRecipients();
    scheduledNoticeStore.getAvailableReports();
});

watch(
    () => props.item,
    () => {
        notice.value = new NoticeModel(props.item);

        if (props.item.type != null && props.item.type != 1) {
            recipientMode.value = "condition";
        } else {
            recipientMode.value = "agency";
        }
    }
);

watch(recipientMode, () => {
    if (recipientMode.value == "agency") {
        notice.value.condition = undefined;
    } else {
        notice.value.agency = undefined;
    }
});

watch(
    () => props.showModal,
    (val) => {
       showCreateNoticeModal.value = props.showModal;
    },
    { immediate: true }
);

</script>
<style>
.width-75 {
    width: 75px;
}
.select-container{
    width: 100%;
}
</style>
